import type { ReactNode } from "react";

export type Tone = "neutral" | "success" | "warning" | "danger" | "info";
export type ToastType = "success" | "error" | "info";
export type ToastState = {
  id: number;
  message: string;
  type: ToastType;
  leaving?: boolean;
};

export const TOAST_VISIBLE_MS = 2700;
export const TOAST_EXIT_MS = 300;

export type ToastTimerApi = {
  setTimeout: (callback: () => void, delay: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
};

export function scheduleToastDismissal(
  onExit: () => void,
  onDismiss: () => void,
  timers: ToastTimerApi = globalThis
) {
  let dismissTimer: ReturnType<typeof setTimeout> | undefined;
  const exitTimer = timers.setTimeout(() => {
    onExit();
    dismissTimer = timers.setTimeout(onDismiss, TOAST_EXIT_MS);
  }, TOAST_VISIBLE_MS);

  return () => {
    timers.clearTimeout(exitTimer);
    if (dismissTimer !== undefined) timers.clearTimeout(dismissTimer);
  };
}
export type MetricCardItem = {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
};

function toneClass(tone: Tone): string {
  if (tone === "success") return "status-badge status-badge-success";
  if (tone === "warning") return "status-badge status-badge-warning";
  if (tone === "danger") return "status-badge status-badge-danger";
  if (tone === "info") return "status-badge status-badge-info";
  return "status-badge";
}

export function StatusBadge({ label, tone = "neutral" }: { label: string; tone?: Tone }) {
  return <span className={`${toneClass(tone)} status-badge-nowrap`}>{label}</span>;
}

export function Toast({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }) {
  const isError = toast.type === "error";

  return (
    <div
      className={`toast toast--${toast.type}${toast.leaving ? " toast--leaving" : ""}`}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      aria-atomic="true"
    >
      <span className="toast__icon" aria-hidden="true">{isError ? "!" : toast.type === "success" ? "✓" : "i"}</span>
      <span>{toast.message}</span>
      <button className="toast__dismiss" type="button" onClick={onDismiss} aria-label="Dismiss notification">×</button>
    </div>
  );
}

export function MetricCard({
  label,
  value,
  hint,
  tone = "neutral"
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) {
  return (
    <article className="metric-card">
      <div className="metric-card__body">
        <span className="metric-card__label">{label}</span>
        <div className="metric-card__value">{value}</div>
      </div>
      {hint && <div className={`metric-card__hint metric-card__hint-${tone}`}>{hint}</div>}
    </article>
  );
}

export function MetricCardGroup({
  items,
  className = ""
}: {
  items: MetricCardItem[];
  className?: string;
}) {
  return (
    <div className={`metric-grid metric-grid-compact ${className}`.trim()}>
      {items.map((item) => (
        <MetricCard
          key={item.label}
          label={item.label}
          value={item.value}
          hint={item.hint}
          tone={item.tone}
        />
      ))}
    </div>
  );
}

export function Panel({
  title,
  description,
  actions,
  children,
  compact = false
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  compact?: boolean;
}) {
  return (
    <section className={`panel ${compact ? "panel-compact" : ""}`}>
      <div className="panel-header panel-header-top">
        <div>
          <h2>{title}</h2>
          {description && <p className="panel-copy">{description}</p>}
        </div>
        {actions && <div className="panel-actions">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function PageShell({
  eyebrow,
  title,
  description,
  actions,
  children
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="page dashboard-page">
      <div className="page-title-row page-title-stack">
        <div>
          {eyebrow && <div className="eyebrow">{eyebrow}</div>}
          <h1>{title}</h1>
          {description && <p>{description}</p>}
        </div>
        {actions && <div className="button-row">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function FormField({
  label,
  hint,
  children,
  wide = false
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={wide ? "form-field form-field-wide" : "form-field"}>
      <span className="form-field__label">{label}</span>
      {children}
      {hint && <small className="form-field__hint">{hint}</small>}
    </label>
  );
}
